/**
 * @file    DynamicTableOld.tsx
 * @scope   SHARED
 * @summary Orchestrator of the legacy (react-data-table) DynamicTable widget. Composes the
 *          hooks shared with DynamicTable plus its own grid pieces. Owns no effect.
 * @consumes DynamicTable/common, ./common, ./Views
 * @produces The DynamicTableOld widget.
 * @sideEffects None directly — see the hooks it composes.
 */
import './common/styles/DynamicTableOld.scss';
import React, { useRef, useState } from 'react';
import { TableColumn } from 'react-data-table-component';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { BaseWidget, TableType, WidgetAction } from 'models/WidgetModel';
import { exportHtmlFile } from 'utils/exportingFiles';
import { exportToImage } from 'utils/exportingToImage';
import { TableState } from 'components/DynamicTable/common/types';
import { prepareEnterpriseData } from 'components/DynamicTable/common/utils/DynamicTableUtils';
import { openSurveyLink } from 'components/DynamicTable/common/utils/cellFormatting';
import { buildLineChartOptions } from 'components/DynamicTable/common/utils/lineChartOptionsBuilder';
import { hasSubtitle, joinClasses } from 'components/DynamicTable/common/utils/tableLayout';
import { useEyeIcon } from 'components/DynamicTable/common/hooks/useEyeIcon';
import { useFetchTriggers } from 'components/DynamicTable/common/hooks/useFetchTriggers';
import { useLineChartSettings } from 'components/DynamicTable/common/hooks/useLineChartSettings';
import { useTableFetch } from 'components/DynamicTable/common/hooks/useTableFetch';
import { useTableLayout } from 'components/DynamicTable/common/hooks/useTableLayout';
import { useTableResponseSync } from 'components/DynamicTable/common/hooks/useTableResponseSync';
import {
  useWidgetActions,
  WidgetActionHandlers,
} from 'components/DynamicTable/common/hooks/useWidgetActions';
import TableHeaderInfo from 'components/DynamicTable/Views/TableHeaderInfo/TableHeaderInfo';
import TableFootNote from 'components/DynamicTable/Views/TableFootNote/TableFootNote';
import { useDataTableProps } from './common/hooks/useDataTableProps';
import { exportTableToCSV, exportTableToExcel } from './common/utils/exporters';
import { getLegacyLineChartData } from './common/utils/lineChartData';
import { resolveLegacySortingType } from './common/utils/sorting';
import { buildLegacyColumns } from './Views/columnsBuilder';
import LegacyTableBody from './Views/LegacyTableBody/LegacyTableBody';

type LegacyTable = TableState<TableColumn<any>>;

const DynamicTableOld: React.FC<BaseWidget> = ({
  response: defaultResponse,
  payload,
  options,
  customWidgetConfig,
  ...props
}) => {
  const { loading } = props;
  const { chartView, selectedValueL1, showLineChart: contextShowLineChart } = useWidgetContext();
  const { pagination, fixedHeader, showTotals, sortServer } = options?.table || {};
  const { displayTimePeriodDelta = false, decimalPlaces } = options || {};

  const [table, setTable] = useState<LegacyTable>();
  const echartRef = useRef<any>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);
  const totalsFirstRow = useRef<any>();
  totalsFirstRow.current = table?.totalHeader;
  const eyeIconBase64 = useEyeIcon();

  const { response, getData, controllerNameValue } = useTableFetch({
    payload,
    paginated: !!(pagination || sortServer),
    defaultPerPage: table?.paginationPerPage || 5,
    firstRender,
  });

  const setTableData = (res: any) => {
    if (!res?.data?.columns) return;
    const data = res.data.data;
    const { type, sortingType } = resolveLegacySortingType(payload);
    res.type = type;
    res.sortingType = sortingType;
    // TODO: this may be done by the mongodb queries in the near future.
    if (sortingType === TableType.ENTERPRISE) prepareEnterpriseData(data);

    const columns = buildLegacyColumns(res.data, sortingType, {
      payload,
      activeResponse: response ? response : defaultResponse,
      selectedValueL1,
      decimalPlaces,
      displayTimePeriodDelta,
    });

    setTable(() => ({
      rawData: res.data,
      columns,
      data,
      totalHeader: showTotals && data instanceof Array && data.length ? data[0] : null,
      paginationTotalRows: res.data.total,
      paginationPerPage: res.data.per_page,
      stats: res.data.summaryStats,
      sortingType,
      type,
    }));
  };

  const { chartData } = useTableResponseSync({
    defaultResponse,
    response,
    decimalPlaces,
    displayTimePeriodDelta,
    firstRender,
    onTableData: setTableData,
  });

  const { showLineChart, setShowLineChart, yAxisValues, goalValues, drawGoalLine } =
    useLineChartSettings(options);

  const displayName = props.displayName || '';
  const actionHandlers: WidgetActionHandlers = {
    [WidgetAction.SWITCH_CHART_TABLE]: () => setShowLineChart(false),
    [WidgetAction.SWITCH_LINECHART_TABLE]: () => setShowLineChart(contextShowLineChart ?? false),
    [WidgetAction.SWITCH_BAR_LINE]: () => undefined,
    [WidgetAction.SEARCH_FILTER]: () => {
      firstRender.current = false;
      getData(1);
    },
    [WidgetAction.EXPORT_TO_CSV]: () =>
      exportTableToCSV(table, displayName, showTotals, customWidgetConfig),
    [WidgetAction.EXPORT_TO_XLSX]: () =>
      void exportTableToExcel(table, displayName, showTotals, customWidgetConfig),
    [WidgetAction.EXPORT_TO_HTML]: () => exportHtmlFile(table, displayName, customWidgetConfig),
    [WidgetAction.EXPORT_TO_PNG]: () =>
      exportToImage(props.displayName, false, props?.widgetId, showLineChart),
    [WidgetAction.EXPORT_TO_JPEG]: () =>
      exportToImage(props.displayName, true, props?.widgetId, showLineChart),
  };
  useWidgetActions(actionHandlers);

  const { lineChartHeight, showScroll } = useTableLayout(
    tableRef,
    echartRef,
    showLineChart,
    table?.data
  );
  useFetchTriggers(getData, firstRender);

  // QUIRK: bitwise OR kept from the original implementation.
  const idSurveyPanel = response?.data?.idSurveyPanel | defaultResponse?.data?.idSurveyPanel;
  const { dataTableProps, sortDirection } = useDataTableProps({
    table,
    setTable,
    tableOptions: options?.table,
    hasIdSurveyPanel: !!(response?.data?.idSurveyPanel || defaultResponse?.data?.idSurveyPanel),
    isTableReady: !chartView && !!table?.columns.length && !!(response || defaultResponse),
    firstRender,
    totalsFirstRow,
    getData,
  });

  const lineChartOptions = showLineChart
    ? buildLineChartOptions({
        chartData: getLegacyLineChartData(table),
        rows: table?.data ?? [],
        firstColumnField: (table?.columns?.[0] as any)?.sortField,
        decimalPlaces,
        goalValues,
        drawGoalLine,
        yAxisValues,
        eyeIconBase64,
      })
    : undefined;

  const bodyClassName = joinClasses(
    'dynamic-table',
    !showLineChart && showScroll && 'sc-container',
    chartView ? 'dt-max-height' : !showLineChart && fixedHeader && 'fixed-header'
  );

  return (
    <div
      className={joinClasses(
        'table-container',
        'old-version',
        hasSubtitle(table?.rawData, customWidgetConfig) && 'separator-line'
      )}
    >
      <TableHeaderInfo
        rawData={table?.rawData}
        stats={table?.stats}
        customWidgetConfig={customWidgetConfig}
        controllerNameValue={controllerNameValue}
        chartView={chartView}
        showTotals={showTotals}
      />
      <div className={bodyClassName} ref={tableRef}>
        <div className={chartView ? 'dt-max-height' : ''}>
          <LegacyTableBody
            showLineChart={showLineChart}
            chartView={chartView}
            echartRef={echartRef}
            lineChartOptions={lineChartOptions}
            lineChartHeight={lineChartHeight}
            chartData={chartData}
            options={options}
            widgetProps={props}
            table={table}
            dataTableProps={dataTableProps}
            sortDirection={sortDirection}
            onRowClicked={(row) => openSurveyLink(row, idSurveyPanel)}
            loading={loading}
          />
        </div>
      </div>
      <TableFootNote footNote={table?.rawData?.footNote} />
    </div>
  );
};

export default DynamicTableOld;
